import './styles.scss';

import { useState, useEffect, useRef } from 'react';
import { Text } from '@fragments';
import {
    IconTrendingUp,
    IconTrendingDown,
    IconMinus,
    IconInfoCircle,
} from '@tabler/icons-react';

function formatValue(value, variant) {
    if (value === null || value === undefined) return '—';
    const num = Number(value);
    if (isNaN(num)) return String(value);

    if (variant === 'money') {
        if (num >= 1_000_000) return `R$${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
        if (num >= 1_000) return `R$${(num / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
        return `R$${num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    if (variant === 'number') {
        if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
        if (num >= 1_000) return `${(num / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
        return String(num);
    }

    return String(value);
}

function resolveGrowthState(growth) {
    if (growth === null || growth === undefined) {
        return { dir: 'neutral', label: null, icon: <IconMinus size={12} />, className: '' };
    }
    if (growth > 0) {
        return { dir: 'positive', label: `+${growth}%`, icon: <IconTrendingUp size={12} color="var(--success)" />, className: 'metric-card__growth--positive' };
    }
    if (growth < 0) {
        return { dir: 'negative', label: `${growth}%`, icon: <IconTrendingDown size={12} color="var(--error)" />, className: 'metric-card__growth--negative' };
    }
    return { dir: 'neutral', label: '0%', icon: <IconMinus size={12} />, className: '' };
}

function Sparkline({ values, color }) {
    if (!values || values.length < 2) return null;

    const W = 98;
    const H = 50;
    const pad = 3;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;

    const pts = values.map((v, i) => {
        const x = (i / (values.length - 1)) * (W - pad * 2) + pad;
        const y = H - pad - ((v - min) / range) * (H - pad * 2);
        return [x, y];
    });

    const d = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    const [lx, ly] = pts[pts.length - 1];

    // Area fill path: line + down to bottom-right + across to bottom-left + close
    const areaD = `${d} L${lx.toFixed(1)},${H} L${pad.toFixed(1)},${H} Z`;

    return (
        <svg
            className="metric-card__sparkline"
            viewBox={`0 0 ${W} ${H}`}
            width={W}
            height={H}
            aria-hidden="true"
        >
            <defs>
                <linearGradient id={`spark-fill-${color.replace(/[^a-zA-Z0-9]/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.18" />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>
            <path
                d={areaD}
                fill={`url(#spark-fill-${color.replace(/[^a-zA-Z0-9]/g, '')})`}
            />
            <path
                d={d}
                fill="none"
                stroke={color}
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <circle cx={lx.toFixed(1)} cy={ly.toFixed(1)} r="2.8" fill={color} />
        </svg>
    );
}

function SlotDigit({ char, duration, animate }) {
    const [display, setDisplay] = useState(char);

    useEffect(() => {
        if (!animate) {
            setDisplay(char);
            return undefined;
        }

        const start = performance.now();
        let timeoutId;

        const tick = () => {
            const elapsed = performance.now() - start;
            if (elapsed >= duration) {
                setDisplay(char);
                return;
            }
            setDisplay(String(Math.floor(Math.random() * 10)));
            // Ease-out: flicker fast early, slow as we approach the target.
            const progress = elapsed / duration;
            const next = 35 + progress * progress * 110;
            timeoutId = setTimeout(tick, next);
        };

        tick();
        return () => clearTimeout(timeoutId);
    }, [animate, char, duration]);

    return <span className="metric-card__slot-digit">{display}</span>;
}

function SlotValue({ text }) {
    const [animate, setAnimate] = useState(false);

    useEffect(() => {
        const id = requestAnimationFrame(() => setAnimate(true));
        return () => cancelAnimationFrame(id);
    }, []);

    // Group consecutive non-digit chars into one static span so the font can
    // still kern them (otherwise "R$", ".", "k" sit visibly loose next to the
    // digits). Each digit stays in its own column so it can roll independently.
    const segments = [];
    let staticBuffer = '';
    let digitIndex = 0;
    const chars = Array.from(String(text));

    const flushBuffer = (key) => {
        if (!staticBuffer) return;
        segments.push({ kind: 'static', text: staticBuffer, key: `s-${key}` });
        staticBuffer = '';
    };

    chars.forEach((char, i) => {
        if (/\d/.test(char)) {
            flushBuffer(i);
            segments.push({ kind: 'digit', char, key: `d-${i}`, idx: digitIndex });
            digitIndex++;
        } else {
            staticBuffer += char;
        }
    });
    flushBuffer('end');

    return (
        <span className="metric-card__slot-value">
            {segments.map((seg) => {
                if (seg.kind === 'static') {
                    return (
                        <span key={seg.key} className="metric-card__slot-static">
                            {seg.text}
                        </span>
                    );
                }
                return (
                    <SlotDigit
                        key={seg.key}
                        char={seg.char}
                        duration={700 + seg.idx * 220}
                        animate={animate}
                    />
                );
            })}
        </span>
    );
}

export default function MetricCard({
    title,
    leftIcon,
    rightIcon,
    value,
    variant = null,
    chartValues,
    growth,
    growthLabel,
    note,
    noteSentiment,
    description,
    loading = false,
    className = '',
}) {
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [reloadEpoch, setReloadEpoch] = useState(0);
    const wasLoading = useRef(loading);

    useEffect(() => {
        if (wasLoading.current && !loading) {
            setReloadEpoch(e => e + 1);
        }
        wasLoading.current = loading;
    }, [loading]);

    const growthState = resolveGrowthState(growth);

    const chartColor =
        growthState.dir === 'positive'
            ? 'var(--success)'
            : growthState.dir === 'negative'
                ? 'var(--error)'
                : 'var(--surface-6)';

    const hasGrowthRow = growth !== undefined && growth !== null;

    return (
        <div className={`metric-card ${className}`.trim()}>
            <div className="metric-card__header">
                <div className="metric-card__title-row">
                    {leftIcon && (
                        <span className="metric-card__left-icon">{leftIcon}</span>
                    )}
                    <Text.span contrast={6} className="metric-card__title">
                        {title}
                    </Text.span>
                    {description && (
                        <span
                            className="metric-card__info-trigger"
                            onMouseEnter={() => setTooltipVisible(true)}
                            onMouseLeave={() => setTooltipVisible(false)}
                        >
                            <IconInfoCircle size={13} />
                            {tooltipVisible && (
                                <span className="metric-card__tooltip" role="tooltip">
                                    {description}
                                </span>
                            )}
                        </span>
                    )}
                </div>
                {rightIcon && (
                    <span className="metric-card__right-icon">{rightIcon}</span>
                )}
            </div>

            <div className="metric-card__body">
                <Text.h2 contrast={9} weight={500} className="metric-card__value">
                    <SlotValue key={`${value}-${variant}-${reloadEpoch}`} text={formatValue(value, variant)} />
                </Text.h2>
                {chartValues && (
                    <Sparkline values={chartValues} color={chartColor} />
                )}
            </div>

            {hasGrowthRow && (
                <div className={`metric-card__growth ${growthState.className}`}>
                    <span className="metric-card__growth-icon">{growthState.icon}</span>
                    <Text.span contrast={5} className="metric-card__growth-label">
                        {growthState.label}
                        {growthLabel && (
                            <span className="metric-card__growth-period"> {growthLabel}</span>
                        )}
                    </Text.span>
                </div>
            )}

            {!hasGrowthRow && note && (
                <div className={`metric-card__note${noteSentiment ? ` metric-card__note--${noteSentiment}` : ''}`}>
                    <Text.span contrast={5}>{note}</Text.span>
                </div>
            )}
        </div>
    );
}
